import { useCallback, useEffect, useRef } from 'react';
import type { FC } from 'react';

import { Card } from '../../Card/Card';

import * as styles from './CardsGallery.module.scss';

export interface CardsGalleryProps {
  cards: { name: string; image: string }[];
  selectedIndex: number;
  onSelectedIndexChange: (value: number) => void;
}

export const CardsGallery: FC<CardsGalleryProps> = ({
  cards,
  selectedIndex,
  onSelectedIndexChange,
}) => {
  const carouselRef = useRef<HTMLUListElement>(null!);
  const initialIndexRef = useRef(selectedIndex);

  if (selectedIndex === -1 || initialIndexRef.current === -1) {
    initialIndexRef.current = selectedIndex;
  }

  const initialIndex = initialIndexRef.current;

  useEffect(() => {
    if (initialIndex !== -1) {
      carouselRef.current.children[initialIndex].scrollIntoView?.();
    }
  }, [initialIndex, cards.length]);

  const handleIntersectionChange = useCallback(
    (entry: IntersectionObserverEntry, index: number) => {
      if (entry.intersectionRatio > 0.85) {
        onSelectedIndexChange(index);
      }
    },
    [onSelectedIndexChange]
  );

  return (
    <div className={styles.container}>
      <ul className={styles.carousel} ref={carouselRef}>
        {cards.map(({ name, image }, index) => (
          // eslint-disable-next-line jsx-a11y/role-supports-aria-props,jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
          <li
            className={styles.item}
            aria-selected={index === selectedIndex}
            key={index}
            onClick={(e) => {
              if ('scrollIntoView' in e.currentTarget) {
                onSelectedIndexChange(index);
                e.currentTarget.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <Card
              onIntersectionChange={handleIntersectionChange}
              index={index}
              imageUrl={image}
              name={name}
            />
          </li>
        ))}
      </ul>
      <div className={styles.pagination}>
        <div>
          {selectedIndex + 1} / {cards.length}
        </div>
      </div>
    </div>
  );
};
